// src/pages/DashboardPage.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmModal from "../components/ConfirmModal";
import { useAuth } from "../context/AuthContext";
import { FiPlus, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { PencilSquareIcon, TrashIcon, MagnifyingGlassIcon, DocumentDuplicateIcon } from "@heroicons/react/24/solid";
import Spinner from "../components/Spinner";
import EventForm from "../components/EventForm";
import {
  fetchEvents as fetchEventsAPI,
  createEvent as createEventAPI,
  updateEvent as updateEventAPI,
  deleteEvent as deleteEventAPI,
  fetchUserDefaults as fetchUserDefaultsAPI,
  getEventStatus,
} from "../utils/api";

// import SubscriptionBanner from '../components/SubscriptionBanner';

function DashboardPage() {
  const { user, isAuthenticated, isInitializing } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Список событий
  const [events, setEvents] = useState([]);

  // Модальные окна
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false); // for copying events
  const [showConfirmModal, setShowConfirmModal] = useState(false); // для одиночного удаления
  const [eventToDelete, setEventToDelete] = useState(null);
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false); // для массового удаления

  // Сортировка для будущих событий
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [stateIndex, setStateIndex] = useState(0);

  // Сортировка для прошедших событий
  const [pastSortBy, setPastSortBy] = useState("date");
  const [pastSortOrder, setPastSortOrder] = useState("desc");
  const [pastStateIndex, setPastStateIndex] = useState(0);

  // Настройки по умолчанию для пользователя
  const [userDefaults, setUserDefaults] = useState(null);

  // Events for editing and copying
  const [editingEvent, setEditingEvent] = useState(null);
  const [copyingEvent, setCopyingEvent] = useState(null);

  // Состояние для поиска
  const [searchTerm, setSearchTerm] = useState("");

  // Состояния для сворачивания/разворачивания секций
  const [showUpcomingEvents, setShowUpcomingEvents] = useState(true);
  const [showPastEvents, setShowPastEvents] = useState(true);

  // Состояния для выбранных событий (bulk deletion)
  const [selectedPastEventIds, setSelectedPastEventIds] = useState([]);
  const [selectedUpcomingEventIds, setSelectedUpcomingEventIds] = useState([]);
  const [selectedOngoingEventIds, setSelectedOngoingEventIds] = useState([]);

  // Активный вид для фильтрации событий
  const [activeView, setActiveView] = useState("all");

  // Функция для определения, мобильное ли устройство
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  // Функция для форматирования даты в зависимости от устройства
  const formatDate = (date) => {
    const dateObj = new Date(date);
    if (isMobile()) {
      return dateObj.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });
    } else {
      return dateObj.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  };

  // Загрузка настроек по умолчанию пользователя
  useEffect(() => {
    const loadUserDefaults = async () => {
      try {
        const res = await fetchUserDefaultsAPI(token);
        if (res.ok) {
          const data = await res.json();
          setUserDefaults({
            defaultCurrency: data.default_currency,
            defaultStartTime: data.default_start_time,
            defaultEventDuration: data.default_event_duration,
            defaultAdmission: data.default_admission ,
          });
        } else {
          toast.error("Failed to load user defaults.");
        }
      } catch (error) {
        toast.error("An error occurred while loading user defaults.");
      }
    };
    if (!isInitializing && isAuthenticated) {
      loadUserDefaults();
    }
  }, [isInitializing, isAuthenticated, token]);

  // Функция для загрузки списка событий
  const loadEvents = useCallback(async () => {
    try {
      const response = await fetchEventsAPI(token);
      if (response.status === 401) {
        navigate("/login");
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      } else {
        // Обработка ошибки загрузки событий
      }
    } catch (error) {
      // Обработка ошибки загрузки событий
    }
  }, [token, navigate]);

  useEffect(() => {
    if (isInitializing) return;
    if (!isAuthenticated) {
      navigate("/login");
    } else {
      loadEvents();
    }
  }, [isInitializing, isAuthenticated, navigate, loadEvents]);

  if (isInitializing) {
    return (
      <div className="container mx-auto max-w-5xl p-4 mt-16 flex justify-center items-center">
        <Spinner />
      </div>
    );
  }

  // Фильтрация событий по поисковой строке
  const filteredEvents = events.filter((event) =>
    event.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Определение групп событий
  // const nowDate = new Date();
  // const upcomingEvents = filteredEvents.filter((event) => {
  //   const start = new Date(event.start_time);
  //   let end;
  //   if (event.end_time) {
  //     end = new Date(event.end_time);
  //   } else {
  //     const duration = parseFloat(userDefaults?.defaultEventDuration) || 24;
  //     end = new Date(start.getTime() + duration * 60 * 60 * 1000);
  //   }
  //   return nowDate <= end;
  // });
  // const pastEvents = filteredEvents.filter((event) => {
  //   const start = new Date(event.start_time);
  //   let end;
  //   if (event.end_time) {
  //     end = new Date(event.end_time);
  //   } else {
  //     const duration = parseFloat(userDefaults?.defaultEventDuration) || 24;
  //     end = new Date(start.getTime() + duration * 60 * 60 * 1000);
  //   }
  //   return nowDate > end;
  // });
  // // Определение текущих (ongoing) событий
  // const ongoingEvents = upcomingEvents.filter((event) =>
  //   event.end_time
  //     ? new Date(event.start_time) < nowDate && nowDate < new Date(event.end_time)
  //     : false
  // );
  // const nonOngoingUpcomingEvents = upcomingEvents.filter(
  //   (event) => !ongoingEvents.includes(event)
  // );
  const upcomingEvents = filteredEvents.filter((event) => 
    getEventStatus(event, userDefaults?.defaultEventDuration) !== "past"
  );
  const pastEvents = filteredEvents.filter((event) => 
    getEventStatus(event, userDefaults?.defaultEventDuration) === "past"
  );
  const ongoingEvents = upcomingEvents.filter((event) => 
    getEventStatus(event, userDefaults?.defaultEventDuration) === "ongoing"
  );
  const nonOngoingUpcomingEvents = upcomingEvents.filter((event) => 
    getEventStatus(event, userDefaults?.defaultEventDuration) === "upcoming"
  );

  // Сортировка будущих событий
  const sortedUpcomingEvents = [...nonOngoingUpcomingEvents].sort((a, b) => {
    if (sortBy === "date") {
      const aDate = new Date(a.start_time).getTime();
      const bDate = new Date(b.start_time).getTime();
      return sortOrder === "asc" ? aDate - bDate : bDate - aDate;
    }
    if (sortBy === "name") {
      return sortOrder === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (sortBy === "state") {
      const sequences = [
        ["confirmed", "planned", "cancelled"],
        ["planned", "confirmed", "cancelled"],
        ["cancelled", "planned", "confirmed"],
      ];
      const seq = sequences[stateIndex];
      const aPos = seq.indexOf(a.state) === -1 ? 999 : seq.indexOf(a.state);
      const bPos = seq.indexOf(b.state) === -1 ? 999 : seq.indexOf(b.state);
      return aPos - bPos;
    }
    return 0;
  });

  // Сортировка прошедших событий
  const sortedPastEvents = [...pastEvents].sort((a, b) => {
    if (pastSortBy === "date") {
      const aDate = new Date(a.start_time).getTime();
      const bDate = new Date(b.start_time).getTime();
      return pastSortOrder === "asc" ? aDate - bDate : bDate - aDate;
    }
    if (pastSortBy === "name") {
      return pastSortOrder === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    if (pastSortBy === "state") {
      const sequences = [
        ["confirmed", "planned", "cancelled"],
        ["planned", "confirmed", "cancelled"],
        ["cancelled", "planned", "confirmed"],
      ];
      const seq = sequences[pastStateIndex];
      const aPos = seq.indexOf(a.state) === -1 ? 999 : seq.indexOf(a.state);
      const bPos = seq.indexOf(b.state) === -1 ? 999 : seq.indexOf(b.state);
      return aPos - bPos;
    }
    return 0;
  });

  // Обработчики сортировки для будущих событий
  const handleSortUpcoming = (criteria) => {
    if (criteria === sortBy) {
      if (criteria === "date" || criteria === "name") {
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else if (criteria === "state") {
        setStateIndex((prev) => (prev + 1) % 3);
      }
    } else {
      setSortBy(criteria);
      if (criteria === "date" || criteria === "name") {
        setSortOrder("asc");
      }
      if (criteria === "state") {
        setStateIndex(0);
      }
    }
  };

  const renderSortIconUpcoming = (column) => {
    if (column !== sortBy) {
      return <span className="ml-1 text-gray-400">–</span>;
    }
    if (column === "date" || column === "name") {
      return sortOrder === "asc" ? "▲" : "▼";
    }
    if (stateIndex === 0) {
      return "▲";
    } else if (stateIndex === 1) {
      return "▼";
    } else {
      return "—";
    }
  };

  // Обработчики сортировки для прошедших событий
  const handlePastSort = (criteria) => {
    if (criteria === pastSortBy) {
      if (criteria === "date" || criteria === "name") {
        setPastSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else if (criteria === "state") {
        setPastStateIndex((prev) => (prev + 1) % 3);
      }
    } else {
      setPastSortBy(criteria);
      if (criteria === "date" || criteria === "name") {
        setPastSortOrder("asc");
      }
      if (criteria === "state") {
        setPastStateIndex(0);
      }
    }
  };

  const renderPastSortIcon = (column) => {
    if (column !== pastSortBy) {
      return <span className="ml-1 text-gray-400">–</span>;
    }
    if (column === "date" || column === "name") {
      return pastSortOrder === "asc" ? "▲" : "▼";
    }
    if (pastStateIndex === 0) {
      return "▲";
    } else if (pastStateIndex === 1) {
      return "▼";
    } else {
      return "—";
    }
  };

  // Обработчики для массового удаления событий
  const handleCheckboxChangeUpcoming = (eventId) => {
    setSelectedUpcomingEventIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  const handleCheckboxChangePast = (eventId) => {
    setSelectedPastEventIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  const handleCheckboxChangeOngoing = (eventId) => {
    setSelectedOngoingEventIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  // Функция для комбинированного удаления выбранных событий (Будущие + Прошедшие + Текущие)
  const handleDeleteSelectedCombinedEvents = async () => {
    try {
      const combinedIds = [
        ...selectedUpcomingEventIds,
        ...selectedPastEventIds,
        ...selectedOngoingEventIds,
      ];
      const deletePromises = combinedIds.map((id) => deleteEventAPI(id, token));
      await Promise.all(deletePromises);
      toast.success("Selected events deleted successfully!");
      setSelectedUpcomingEventIds([]);
      setSelectedPastEventIds([]);
      setSelectedOngoingEventIds([]);
      loadEvents();
    } catch (error) {
      toast.error("Failed to delete selected events.");
    }
  };

  // Функция для создания события
  const handleCreateClick = () => {
    setShowCreateModal(true);
  };

  const handleCreateEvent = async (data) => {
    try {
      const eventToSubmit = {
        ...data,
        currency: data.currency || userDefaults?.defaultCurrency || "USD",
        event_duration: parseFloat(data.event_duration) || 24,
      };
  
      // Всегда рассчитываем end_time на основе start_time и duration
      if (eventToSubmit.start_time) {
        const startTime = new Date(eventToSubmit.start_time);
        const durationHours = parseFloat(eventToSubmit.event_duration);
        const endTime = new Date(startTime.getTime() + durationHours * 60 * 60 * 1000);
        eventToSubmit.end_time = endTime.toISOString();
      }

      const res = await createEventAPI(eventToSubmit, token);
      if (res.ok) {
        toast.success("Event created successfully!");
        setShowCreateModal(false);
        loadEvents();
      } else {
        const errorData = await res.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      toast.error("An error occurred while creating the event.");
    }
  };

  // Handle copy event action - FIXED: Properly handle duration
  const handleCopyClick = (event) => {
    // Keep original date and time (validation will happen in EventForm)
    const originalDateTime = event.start_time;
    
    // Calculate duration from start_time and end_time if available
    let calculatedDuration = null;
    if (event.start_time && event.end_time) {
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);
      const durationMs = end - start;
      calculatedDuration = (durationMs / (1000 * 60 * 60)); // Duration in hours
    }
    
    setCopyingEvent({
      ...event,
      // Keep the original name (no "Copy of" prefix)
      name: event.name,
      // Keep original start_time
      start_time: originalDateTime,
      // Use calculated duration or fallback to event_duration or defaults
      event_duration: calculatedDuration || event.event_duration || userDefaults?.defaultEventDuration || 20
    });
    setShowCopyModal(true);
  };
  
  // Handle copying an event - FIXED: Properly handle duration and end_time calculation
  const handleCopyEvent = async (data) => {
    try {
      // Create event object with correct duration handling
      const eventToSubmit = {
        ...data,
        currency: data.currency || userDefaults?.defaultCurrency || "USD",
        start_time: data.start_time,
        event_duration: parseFloat(data.event_duration) // Ensure it's a number
      };

      // Always calculate end_time based on start_time and event_duration
      if (eventToSubmit.start_time && eventToSubmit.event_duration) {
        const startTime = new Date(eventToSubmit.start_time);
        const durationHours = parseFloat(eventToSubmit.event_duration);
        const endTime = new Date(startTime.getTime() + durationHours * 60 * 60 * 1000);
        eventToSubmit.end_time = endTime.toISOString();
      }

      const res = await createEventAPI(eventToSubmit, token);
      if (res.ok) {
        toast.success("Event copied successfully!");
        setShowCopyModal(false);
        setCopyingEvent(null);
        loadEvents();
      } else {
        const errorData = await res.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      toast.error("An error occurred while copying the event.");
    }
  };

  // Функции для редактирования события
  const handleEditClick = (event) => {
    // Calculate duration from start_time and end_time if availablebg
    let calculatedDuration = null;
    if (event.start_time && event.end_time) {
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);
      const durationMs = end - start;
      calculatedDuration = (durationMs / (1000 * 60 * 60)); // Hours
    }
    
    setEditingEvent({
      ...event,
      // Use calculated duration or fallback to event_duration
      event_duration: calculatedDuration || event.event_duration
    });
    setShowEditModal(true);
  };

  const handleUpdateEvent = async (updatedData) => {
    try {
      // Calculate end_time based on start_time and event_duration
      const dataToUpdate = {...updatedData};
      if (dataToUpdate.start_time && dataToUpdate.event_duration) {
        const startTime = new Date(dataToUpdate.start_time);
        const durationHours = parseFloat(dataToUpdate.event_duration);
        const endTime = new Date(startTime.getTime() + durationHours * 60 * 60 * 1000);
        dataToUpdate.end_time = endTime.toISOString();
      }
      
      const res = await updateEventAPI(editingEvent.id, dataToUpdate, token);
      if (res.ok) {
        toast.success("Event updated successfully!");
        setShowEditModal(false);
        setEditingEvent(null);
        loadEvents();
      } else {
        const errorData = await res.json();
        toast.error(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      toast.error("An error occurred while updating the event.");
    }
  };

  // Функции для одиночного удаления события
  const handleDeleteClick = (eventId) => {
    setEventToDelete(eventId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await deleteEventAPI(eventToDelete, token);
      if (res.ok) {
        toast.success("Event deleted successfully!");
        loadEvents();
      } else {
        toast.error("Failed to delete event");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the event.");
    } finally {
      setShowConfirmModal(false);
      setEventToDelete(null);
    }
  };

  // Функция для подтверждения массового удаления выбранных событий
  const confirmBulkDelete = async () => {
    await handleDeleteSelectedCombinedEvents();
    setShowBulkConfirmModal(false);
  };

  // Определяем, какие секции показывать на основе activeView
  const shouldShowOngoingEvents = activeView === "all" || activeView === "ongoing";
  const shouldShowUpcomingEvents = activeView === "all" || activeView === "upcoming";
  const shouldShowPastEvents = activeView === "all" || activeView === "past";

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto max-w-5xl p-4 mt-16" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
        {/* Модернизированная шапка */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold mb-6 text-gray-800">
            {user?.name ? `${user.name}` : "Hello"}, welcome to your Dashboard!
          </h1>

          {/* Добавляем баннер подписки */}
  {/* <SubscriptionBanner /> */}
          
          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 mb-6">
  <nav className="flex space-x-8">
    {/* Кнопка All Events с условным текстом */}
    <button
      onClick={() => setActiveView("all")}
      className={`pb-4 px-1 ${
        activeView === "all" 
          ? "border-b-2 border-indigo-500 text-indigo-600 font-medium" 
          : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
    >
      {/* Если мобильное устройство — «All», иначе — «All Events» */}
      {isMobile() ? "All" : "All Events"}
    </button>

    {/* Оставшиеся кнопки вообще не трогаем */}
    <button
      onClick={() => setActiveView("ongoing")}
      className={`pb-4 px-1 ${
        activeView === "ongoing" 
          ? "border-b-2 border-green-500 text-green-600 font-medium" 
          : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
    >
      Ongoing
    </button>

    <button
      onClick={() => setActiveView("upcoming")}
      className={`pb-4 px-1 ${
        activeView === "upcoming" 
          ? "border-b-2 border-purple-500 text-purple-600 font-medium" 
          : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
    >
      Upcoming
    </button>

    <button
      onClick={() => setActiveView("past")}
      className={`pb-4 px-1 ${
        activeView === "past" 
          ? "border-b-2 border-gray-500 text-gray-600 font-medium" 
          : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
      }`}
    >
      Past
    </button>
  </nav>
</div>
          
          {/* Информационные карточки */}
          <div className="grid grid-cols-3 gap-1 sm:gap-4 mb-6">
            <div 
              onClick={() => setActiveView("ongoing")}
              className={`p-2 sm:p-4 rounded-lg transition-colors cursor-pointer ${
                activeView === "ongoing" ? "bg-indigo-50" : "bg-indigo-50 hover:bg-indigo-100"
              }`}
            >
              <p className="text-xs sm:text-sm text-gray-600 mb-0 sm:mb-1">
                <span className="block sm:inline">Ongoing</span>
                <span className="hidden sm:inline"> Events</span>
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">{ongoingEvents.length}</p>
            </div>
            
            <div 
              onClick={() => setActiveView("upcoming")}
              className={`p-2 sm:p-4 rounded-lg  transition-colors cursor-pointer ${
                activeView === "upcoming" ? "bg-indigo-50" : "bg-indigo-50 hover:bg-indigo-100"
              }`}
            >
              <p className="text-xs sm:text-sm text-gray-600 mb-0 sm:mb-1">
                <span className="block sm:inline">Upcoming</span>
                <span className="hidden sm:inline"> Events</span>
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">{nonOngoingUpcomingEvents.length}</p>
            </div>
            
            <div 
    onClick={() => setActiveView("past")}
    className={`p-2 sm:p-4 rounded-lg  transition-colors cursor-pointer ${
      activeView === "past" ? "bg-indigo-50" : "bg-indigo-50 hover:bg-indigo-100"
    }`}
  >
    <p className="text-xs sm:text-sm text-gray-600 mb-0 sm:mb-1">
      <span className="block sm:inline">Past</span>
      <span className="hidden sm:inline"> Events</span>
    </p>
    <p className="text-lg sm:text-2xl font-bold text-gray-800">{pastEvents.length}</p>
  </div>
</div>
          
          {/* Действия */}
          <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0">
  <div className="flex space-x-2">
    <button
      onClick={handleCreateClick}
      className="bg-indigo-600 text-white font-bold px-4 py-2 rounded flex items-center hover:bg-indigo-700 transition duration-200"
    >
      <FiPlus className="mr-2 text-lg" />
      Create Event
    </button>
    
    {/* Новая кнопка статистики */}
    {/* <Link 
      to="/statistics"
      className="bg-emerald-600 text-white font-bold px-4 py-2 rounded flex items-center hover:bg-emerald-700 transition duration-200"
    >
      <ChartBarIcon className="h-5 w-5 mr-2" />
      Statistics
    </Link> */}
    
    {(selectedPastEventIds.length > 0 ||
      selectedUpcomingEventIds.length > 0 ||
      selectedOngoingEventIds.length > 0) && (
      <button
        onClick={() => setShowBulkConfirmModal(true)}
        className="flex items-center bg-white text-gray-600 px-4 py-2 rounded hover:bg-gray-50 border border-gray-300 transition duration-200"
      >
        <TrashIcon className="h-5 w-5 mr-2" />
        ({selectedPastEventIds.length + selectedUpcomingEventIds.length + selectedOngoingEventIds.length})
      </button>
    )}
  </div>
            
            {/* Поиск */}
            <div className="relative max-w-md w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search events..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Conditional rendering based on activeView */}
        {/* Ongoing Events Section */}
        {shouldShowOngoingEvents && ongoingEvents.length > 0 && (
          <div className="mb-6 rounded-lg shadow-md">
            <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto] items-center bg-white rounded-t-lg px-4 py-2">
              <div>
                <input
                  type="checkbox"
                  style={{ marginLeft: "5px" }}
                  checked={selectedOngoingEventIds.length === ongoingEvents.length && ongoingEvents.length > 0}
                  onChange={() => {
                    if (selectedOngoingEventIds.length === ongoingEvents.length) {
                      setSelectedOngoingEventIds([]);
                    } else {
                      setSelectedOngoingEventIds(ongoingEvents.map((event) => event.id));
                    }
                  }}
                />
              </div>
              <h3 className="font-semibold text-gray-600 whitespace-nowrap" style={{ fontSize: isMobile() ? '1rem' : '1.125rem' }}>
                Ongoing Events
              </h3>
              <div></div>
              <div></div>
              <div></div>
            </div>
            <div>
              {ongoingEvents.map((event) => (
                <div
                  key={event.id}
                  className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center border-b border-green-200 divide-x divide-green-200 p-2 hover:bg-green-50 transition duration-200 cursor-default bg-green-50"
                >
                  <div className="text-center">
                    <input
                      type="checkbox"
                      className="mx-auto"
                      checked={selectedOngoingEventIds.includes(event.id)}
                      onChange={() => handleCheckboxChangeOngoing(event.id)}
                    />
                  </div>
                  <div className="flex items-center space-x-2 px-2">
                    <span className="w-2 h-2 rounded bg-green-500" />
                    {!isMobile() && <span className="text-sm">ongoing</span>}
                  </div>
                  <div className="text-base font-medium text-black px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                    <Link to={`/events/${event.id}`} className="block" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
                      {event.name}
                    </Link>
                  </div>
                  <div className="text-sm font-medium text-gray-600 text-center px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                    <Link to={`/events/${event.id}`} className="block" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
                      {formatDate(event.start_time)}
                    </Link>
                  </div>
                  <div className="flex space-x-2 justify-center px-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleCopyClick(event);
                      }}
                      className="text-gray-500 hover:text-blue-600"
                      title="Copy Event"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleEditClick(event);
                      }}
                      className="text-gray-500 hover:text-yellow-700"
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteClick(event.id);
                      }}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Events Section */}
        {shouldShowUpcomingEvents && nonOngoingUpcomingEvents.length > 0 && (
          <div className="my-6 pt-2 rounded-lg shadow-md">
            <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center bg-white rounded-t-lg px-4 py-2">
              <div>
                <input
                  type="checkbox"
                  style={{ marginLeft: "5px" }}
                  checked={selectedUpcomingEventIds.length === nonOngoingUpcomingEvents.length && nonOngoingUpcomingEvents.length > 0}
                  onChange={() => {
                    if (selectedUpcomingEventIds.length === nonOngoingUpcomingEvents.length) {
                      setSelectedUpcomingEventIds([]);
                    } else {
                      setSelectedUpcomingEventIds(nonOngoingUpcomingEvents.map((event) => event.id));
                    }
                  }}
                />
              </div>
              <h3 className="font-semibold text-gray-600 whitespace-nowrap" style={{ fontSize: isMobile() ? '1rem' : '1.125rem' }}>
                Upcoming Events
              </h3>
              <div></div>
              <div></div>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowUpcomingEvents((prev) => !prev)}
                  className="focus:outline-none"
                >
                  {showUpcomingEvents ? (
                    <FiChevronUp className="text-xl" />
                  ) : (
                    <FiChevronDown className="text-xl" />
                  )}
                </button>
              </div>
              <div></div>
            </div>

            {showUpcomingEvents && (
              <>
                <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center font-semibold gap-2 border-b border-gray-200 py-1 px-4" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
                  <div></div>
                  <div
                    className="cursor-pointer text-center"
                    onClick={() => handleSortUpcoming("state")}
                  >
                    State
                    <span className="ml-1 text-gray-600 text-sm" style={{ fontSize: "11px" }}>
                      {renderSortIconUpcoming("state")}
                    </span>
                  </div>
                  <div
                    className="cursor-pointer text-center"
                    onClick={() => handleSortUpcoming("name")}
                  >
                    Event Title
                    <span className="ml-1 text-gray-600 text-sm" style={{ fontSize: "11px" }}>
                      {renderSortIconUpcoming("name")}
                    </span>
                  </div>
                  <div
                    className="cursor-pointer text-center"
                    onClick={() => handleSortUpcoming("date")}
                  >
                    Date
                    <span className="ml-1 text-gray-600 text-sm" style={{ fontSize: "11px" }}>
                      {renderSortIconUpcoming("date")}
                    </span>
                  </div>
                  <div></div>
                </div>
                <div>
                  {sortedUpcomingEvents.map((event) => {
                    const ended = event.end_time
                      ? new Date(event.end_time) < new Date()
                      : false;
                    const displayState = ended ? "ended" : event.state;
                    let stateColor = "bg-gray-300";
                    if (displayState === "planned") stateColor = "bg-blue-500";
                    else if (displayState === "confirmed") stateColor = "bg-green-500";
                    else if (displayState === "cancelled") stateColor = "bg-red-500";
                    else if (displayState === "ended") stateColor = "bg-purple-500";

                    return (
                      <div
                        key={event.id}
                        className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center border-b border-purple-200 divide-x divide-purple-200 p-2 hover:bg-purple-50 transition duration-200 cursor-default bg-purple-50"
                      >
                        <div className="text-center">
                          <input
                            type="checkbox"
                            className="mx-auto"
                            checked={selectedUpcomingEventIds.includes(event.id)}
                            onChange={() => handleCheckboxChangeUpcoming(event.id)}
                          />
                        </div>
                        <div className="flex items-center space-x-2 px-2">
                          <span className={`w-2 h-2 rounded ${stateColor}`} />
                          {!isMobile() && <span className="text-sm">
                            {displayState || "unknown"}
                          </span>}
                        </div>
                        <div className="text-base font-medium text-black px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          <Link to={`/events/${event.id}`} className="block" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
                            {event.name}
                          </Link>
                        </div>
                        <div className="text-sm font-medium text-gray-600 text-center px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          <Link to={`/events/${event.id}`} className="block" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
                            {formatDate(event.start_time)}
                          </Link>
                        </div>
                        <div className="flex space-x-2 justify-center px-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleCopyClick(event);
                            }}
                            className="text-gray-500 hover:text-blue-600"
                            title="Copy Event"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleEditClick(event);
                            }}
                            className="text-gray-500 hover:text-yellow-700"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleDeleteClick(event.id);
                            }}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Past Events Section */}
        {shouldShowPastEvents && pastEvents.length > 0 && (
          <div className="my-6 pt-2 rounded-lg shadow-md">
            <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center bg-white rounded-t-lg px-4 py-2">
              <div>
                <input
                  type="checkbox"
                  style={{ marginLeft: "5px" }}
                  checked={selectedPastEventIds.length === pastEvents.length && pastEvents.length > 0}
                  onChange={() => {
                    if (selectedPastEventIds.length === pastEvents.length) {
                      setSelectedPastEventIds([]);
                    } else {
                      setSelectedPastEventIds(pastEvents.map((event) => event.id));
                    }
                  }}
                />
              </div>
              <h3 className="font-semibold text-gray-600 whitespace-nowrap" style={{ fontSize: isMobile() ? '1rem' : '1.125rem' }}>
                Past Events
              </h3>
              <div></div>
              <div></div>
              <div className="flex justify-center">
                <button
                  onClick={() => setShowPastEvents((prev) => !prev)}
                  className="focus:outline-none"
                >
                  {showPastEvents ? (
                    <FiChevronUp className="text-xl" />
                  ) : (
                    <FiChevronDown className="text-xl" />
                  )}
                </button>
              </div>
              <div></div>
            </div>

            {showPastEvents && (
              <>
                <div className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center font-semibold gap-2 border-b border-gray-200 py-1 px-4" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
                  <div></div>
                  <div
                    className="cursor-pointer text-center"
                    onClick={() => handlePastSort("state")}
                  >
                    State
                    <span className="ml-1 text-gray-600 text-sm" style={{ fontSize: "11px" }}>
                      {renderPastSortIcon("state")}
                    </span>
                  </div>
                  <div
                    className="cursor-pointer text-center"
                    onClick={() => handlePastSort("name")}
                  >
                    Event Title
                    <span className="ml-1 text-gray-600 text-sm" style={{ fontSize: "11px" }}>
                      {renderPastSortIcon("name")}
                    </span>
                  </div>
                  <div
                    className="cursor-pointer text-center"
                    onClick={() => handlePastSort("date")}
                  >
                    Date
                    <span className="ml-1 text-gray-600 text-sm" style={{ fontSize: "11px" }}>
                      {renderPastSortIcon("date")}
                    </span>
                  </div>
                  <div></div>
                </div>
                <div>
                  {sortedPastEvents.map((event) => {
                    // const ended = (() => {
                    //   if (event.end_time) {
                    //     return new Date(event.end_time) < new Date();
                    //   } else {
                    //     const duration = parseFloat(userDefaults?.defaultEventDuration) || 24;
                    //     const startTime = new Date(event.start_time);
                    //     const endTimeThreshold = new Date(startTime.getTime() + duration * 60 * 60 * 1000);
                    //     return new Date() > endTimeThreshold;
                    //   }
                    // })();

                    const ended = (() => {
                      return getEventStatus(event, userDefaults?.defaultEventDuration) === "past";
                    })();
                    const pastDisplayState = ended ? "ended" : event.state;
                    let pastStateColor = "bg-gray-300";
                    if (pastDisplayState === "planned") pastStateColor = "bg-blue-500";
                    else if (pastDisplayState === "confirmed") pastStateColor = "bg-green-500";
                    else if (pastDisplayState === "cancelled") pastStateColor = "bg-red-500";
                    else if (pastDisplayState === "ended") pastStateColor = "bg-purple-500";

                    return (
                      <div
                        key={event.id}
                        className="grid grid-cols-[40px_1fr_4fr_2fr_auto_auto] items-center border-b border-gray-200 divide-x divide-gray-200 p-2 hover:bg-gray-100 transition duration-200 cursor-default bg-gray-50"
                      >
                        <div className="text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="mx-auto"
                            checked={selectedPastEventIds.includes(event.id)}
                            onChange={() => handleCheckboxChangePast(event.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="flex items-center space-x-2 px-2" onClick={(e) => e.stopPropagation()}>
                          <span className={`w-2 h-2 rounded ${pastStateColor}`} />
                          {!isMobile() && <span className="text-sm">{pastDisplayState || "unknown"}</span>}
                        </div>
                        <div className="text-base font-medium text-black px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          <Link to={`/events/${event.id}`} className="block" style={{ fontSize: isMobile() ? '0.9rem' : '1rem' }}>
                            {event.name}
                          </Link>
                        </div>
                        <div className="text-sm font-medium text-gray-600 text-center px-2 overflow-hidden whitespace-nowrap text-ellipsis">
                          <Link to={`/events/${event.id}`} className="block" style={{ fontSize: isMobile() ? '0.8rem' : '0.9rem' }}>
                            {formatDate(event.start_time)}
                          </Link>
                        </div>
                        <div className="flex space-x-2 justify-center px-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleCopyClick(event);
                            }}
                            className="text-gray-500 hover:text-blue-600"
                            title="Copy Event"
                          >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleEditClick(event);
                            }}
                            className="text-gray-500 hover:text-yellow-700"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              handleDeleteClick(event.id);
                            }}
                            className="text-gray-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Пустое состояние, когда по фильтру нет событий */}
        {activeView !== "all" &&
          ((activeView === "ongoing" && ongoingEvents.length === 0) ||
            (activeView === "upcoming" && nonOngoingUpcomingEvents.length === 0) ||
            (activeView === "past" && pastEvents.length === 0)) && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No {activeView} events found</h3>
              <p className="text-gray-500">Try switching to a different view or create a new event.</p>
            </div>
          )}

        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmDelete}
          message="Are you sure you want to delete this event?"
          style={{ fontSize: isMobile() ? '0.8rem' : '1rem' }}
        />

        {/* Модальное окно для подтверждения массового удаления */}
        <ConfirmModal
          isOpen={showBulkConfirmModal}
          onClose={() => setShowBulkConfirmModal(false)}
          onConfirm={confirmBulkDelete}
          message="Are you sure you want to delete the selected events?"
          style={{ fontSize: isMobile() ? '0.8rem' : '1rem' }}
        />

        {showCreateModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <EventForm
              key={showCreateModal ? "createModalOpen" : "createModalClosed"}
              title="Create New Event"
              initialData={(() => {
                const today = new Date().toISOString().split("T")[0];
                const defaultStartTime = userDefaults?.defaultStartTime;
                const startTimeForInitialData = defaultStartTime
                  ? `${today}T${defaultStartTime}`
                  : null;
                const defaultEventDuration = userDefaults?.defaultEventDuration;
                return {
                  name: "",
                  description: "",
                  place: user?.default_place || "",
                  start_time: startTimeForInitialData,
                  state: "confirmed",
                  admission: userDefaults?.defaultAdmission || "",
                  currency: userDefaults?.defaultCurrency || "USD",
                  event_duration: defaultEventDuration
                    ? parseInt(defaultEventDuration, 10)
                    : 20,
                };
              })()}
              onSubmit={handleCreateEvent}
              onCancel={() => setShowCreateModal(false)}
            />
          </div>
        )}

        {showEditModal && editingEvent && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <EventForm
              title="Edit Event"
              initialData={{
                ...editingEvent,
                start_time: editingEvent.start_time || "2025-01-01T20:00",
                event_duration: editingEvent.event_duration,
              }}
              onSubmit={handleUpdateEvent}
              onCancel={() => setShowEditModal(false)}
            />
          </div>
        )}

        {/* Copy Event Modal - Now with properly calculated duration */}
        {showCopyModal && copyingEvent && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <EventForm
              title="Copy Event"
              initialData={{
                ...copyingEvent
              }}
              onSubmit={handleCopyEvent}
              onCancel={() => {
                setShowCopyModal(false);
                setCopyingEvent(null);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;